import { isArabic } from './../../core/helper'

export const PAYMENT_STATUSES = [
  'pending',
  'paid',
  'captured',
  'failed',
  'settled',
  'refunded',
  'cancelled'
]

export const WITHDRAWAL_STATUSES = [
  'pending_review',
  'approved',
  'rejected',
  'processing',
  'completed',
  'failed'
]

const SENSITIVE_KEY = /hmac|secret|api[_-]?key|private[_-]?key|password|token|authorization|cvv|cvc|^pan$|card[_-]?number|raw[_-]?(body|payload)|signature/i

export function isSuccess(res) {
  return !!(res && typeof res.message === 'string' && res.message.toLowerCase() === 'success')
}

export function isForbidden(res) {
  return !!(res && typeof res.message === 'string' && res.message.toLowerCase() === 'forbidden')
}

export function apiErrorMessage(res) {
  if (!res) {
    return 'Something went wrong'
  }
  if (res.errors && typeof res.errors === 'object') {
    const messages = []
    Object.keys(res.errors).forEach((key) => {
      const value = res.errors[key]
      if (Array.isArray(value)) {
        value.forEach((item) => messages.push(item))
      } else if (value) {
        messages.push(value)
      }
    })
    if (messages.length) {
      return messages.join('\n')
    }
  }
  if (res.error && typeof res.error === 'string') {
    return res.error
  }
  if (res.message && res.message.toLowerCase() !== 'success') {
    return res.message
  }
  return 'Something went wrong'
}

export function unwrapData(res) {
  if (!res) {
    return {}
  }
  if (res.data && typeof res.data === 'object') {
    return res.data
  }
  return res
}

export function extractCollection(data, key) {
  if (!data) {
    return []
  }
  if (Array.isArray(data[key])) {
    return data[key]
  }
  return []
}

export function extractPagination(data) {
  const source = data || {}
  const info = source.paginationInfo || {}
  const links = source.paginationLinks || {}
  return {
    currentPage: info.currentPage || 1,
    lastPage: info.lastPage || 1,
    perPage: info.perPage,
    total: info.total,
    previousPages: Array.isArray(links.previousPages) ? links.previousPages.slice() : [],
    nextPages: Array.isArray(links.nextPages) ? links.nextPages.slice() : []
  }
}

export function formatMoney(value) {
  if (value === undefined || value === null || value === '') {
    return 'N/A'
  }
  if (typeof value === 'object') {
    return 'N/A'
  }
  const number = Number(value)
  if (Number.isNaN(number)) {
    return `${value} LE`
  }
  return `${number.toLocaleString()} LE`
}

export function formatDate(value) {
  if (!value) {
    return 'N/A'
  }
  return value
}

export function displayText(value) {
  if (value === undefined || value === null || value === '') {
    return 'N/A'
  }
  return String(value)
}

export function rtlProps(value) {
  const text = displayText(value)
  if (text === 'N/A') {
    return { style: { direction: 'ltr' } }
  }
  try {
    return { style: { direction: isArabic(text) ? 'rtl' : 'ltr' } }
  } catch (e) {
    return { style: { direction: 'ltr' } }
  }
}

export function personName(person) {
  if (!person) {
    return 'N/A'
  }
  if (typeof person === 'string') {
    return person
  }
  const en = person.enName || ''
  const ar = person.arName || ''
  if (en && ar && en !== ar) {
    return `${en} / ${ar}`
  }
  return en || ar || 'N/A'
}

export function customerLabel(item) {
  if (!item) {
    return 'N/A'
  }
  return personName(item.customer)
}

export function technicianLabel(item) {
  if (!item) {
    return 'N/A'
  }
  return personName(item.fixer)
}

export function technicianId(item) {
  if (!item) {
    return ''
  }
  if (item.fixerId !== undefined && item.fixerId !== null && item.fixerId !== '') {
    return item.fixerId
  }
  if (item.fixer && item.fixer.id !== undefined && item.fixer.id !== null) {
    return item.fixer.id
  }
  return ''
}

export function statusValue(item) {
  if (!item) {
    return ''
  }
  const status = item.status !== undefined ? item.status : item
  if (status === undefined || status === null || status === '') {
    return ''
  }
  if (typeof status === 'string') {
    return status
  }
  return ''
}

export function statusLabel(item) {
  const value = statusValue(item)
  return value ? String(value).replace(/_/g, ' ') : 'N/A'
}

export function normalizeStatus(item) {
  return String(statusValue(item) || '').toLowerCase().replace(/\s+/g, '_')
}

export function maskValue(value) {
  if (value === undefined || value === null || value === '') {
    return 'N/A'
  }
  const str = String(value)
  if (str.length <= 4) {
    return str
  }
  return `${'*'.repeat(str.length - 4)}${str.slice(-4)}`
}

export function looksLikeCard(value) {
  const digits = String(value || '').replace(/\s+/g, '')
  return /^\d{12,19}$/.test(digits)
}

export function payoutDetails(item) {
  if (!item) {
    return 'N/A'
  }
  const account = item.payoutAccount || {}
  const type = item.payoutType || account.payoutType || 'N/A'
  const holder = account.accountHolderName || ''
  const bank = account.bankName || ''
  const number = account.maskedAccount || account.accountNumber || account.iban || account.walletNumber || ''
  const masked = number
    ? (String(number).indexOf('*') !== -1 ? number : maskValue(number))
    : 'N/A'
  const parts = [type]
  if (bank) {
    parts.push(bank)
  }
  if (holder) {
    parts.push(holder)
  }
  parts.push(masked)
  return parts.filter(Boolean).join(' · ')
}

function shouldOmitKey(key) {
  return SENSITIVE_KEY.test(String(key || ''))
}

export function sanitizePaymobCallback(value, key) {
  if (value === undefined || value === null) {
    return null
  }
  if (shouldOmitKey(key)) {
    return '[hidden]'
  }
  if (Array.isArray(value)) {
    return value.map((item, index) => sanitizePaymobCallback(item, index))
  }
  if (typeof value === 'object') {
    const clean = {}
    Object.keys(value).forEach((childKey) => {
      if (shouldOmitKey(childKey)) {
        return
      }
      const child = sanitizePaymobCallback(value[childKey], childKey)
      if (child !== undefined) {
        clean[childKey] = child
      }
    })
    return clean
  }
  if (looksLikeCard(value)) {
    return maskValue(value)
  }
  return value
}

export function paymobCallback(item) {
  if (!item || !item.paymobCallback) {
    return null
  }
  return sanitizePaymobCallback(item.paymobCallback)
}

export function flattenSummary(value) {
  if (value === undefined || value === null || value === '') {
    return []
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return [{ label: 'Total', value }]
  }
  return Object.keys(value).map((key) => ({
    label: key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' '),
    value: value[key]
  }))
}

export function recordId(item) {
  if (!item || item.id === undefined || item.id === null || item.id === '') {
    return ''
  }
  return item.id
}

export function optionalNotePayload(note) {
  const trimmed = String(note || '').trim()
  if (!trimmed) {
    return {}
  }
  return { note: trimmed }
}

export function requiredNotePayload(note) {
  return { note: String(note || '').trim() }
}

export function completeWithdrawalPayload(transferReference, note) {
  const payload = {
    transferReference: String(transferReference || '').trim()
  }
  const trimmedNote = String(note || '').trim()
  if (trimmedNote) {
    payload.note = trimmedNote
  }
  return payload
}

export function settlePayload(paymobSettlementReference, notes) {
  const payload = {
    paymobSettlementReference: String(paymobSettlementReference || '').trim()
  }
  const trimmedNotes = String(notes || '').trim()
  if (trimmedNotes) {
    payload.notes = trimmedNotes
  }
  return payload
}

export function withdrawalActions(item) {
  const status = normalizeStatus(item)
  if (status === 'pending_review') {
    return ['approve', 'reject']
  }
  if (status === 'approved') {
    return ['processing', 'fail']
  }
  if (status === 'processing') {
    return ['complete', 'fail']
  }
  return []
}
